import { useEffect, useRef, useState } from "react";
import { Editor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Youtube from "@tiptap/extension-youtube";
import { supabase } from "../supabase/supabaseClient";
import { v4 as uuidv4 } from "uuid";
import PollModal from "./PollModal";

import {
  Link as LinkIcon,
  Youtube as YoutubeIcon,
  BarChartBig,
  Image as ImageIcon,
  Film as VideoIcon,
  Bold,
  Italic,
  List as ListIcon,
} from "lucide-react";

import Video from "./extensions/Video";

export interface TiptapEditorProps {
  content: string;
  setContent: (value: string) => void;
  imageUploading?: boolean;
  onPollChange?: (poll: any | null) => void;
  className?: string;
}

export default function TiptapEditor({
  content,
  setContent,
  imageUploading,
  onPollChange,
  className,
}: TiptapEditorProps) {
  const [editor, setEditor] = useState<Editor | null>(null);
  const [localUploading, setLocalUploading] = useState(false);
  const [poll, setPoll] = useState<any | null>(null);
  const [showPollModal, setShowPollModal] = useState(false);

  const inputImgRef = useRef<HTMLInputElement>(null);
  const inputVideoRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const instance = new Editor({
      extensions: [
        StarterKit,
        Image.configure({ inline: false }),
        Link.configure({
          openOnClick: true,
          autolink: true,
          linkOnPaste: true,
        }),
        Youtube.configure({
          width: 480,
          height: 320,
          controls: true,
          allowFullscreen: true,
        }),
        Video,
      ],
      content,
      onUpdate: ({ editor }) => setContent(editor.getHTML()),
    });

    setEditor(instance);
    return () => {
      instance.destroy();
    };
    // eslint-disable-next-line
  }, []);

  // ✅ 이미지/동영상 업로드
  async function handleMediaUpload(
    e: React.ChangeEvent<HTMLInputElement>,
    type: "image" | "video"
  ) {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    setLocalUploading(true);

    const ext = file.name.split(".").pop();
    const safeName = `${type}_${uuidv4()}.${ext}`;
    const bucket = "editor-images";
    const { error } = await supabase.storage.from(bucket).upload(safeName, file);

    setLocalUploading(false);

    if (error) {
      alert(`${type === "image" ? "이미지" : "동영상"} 업로드 실패: ` + error.message);
      return;
    }

    const { data: publicUrlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(safeName);
    const url = publicUrlData?.publicUrl;

    if (url) {
      if (type === "image") {
        editor.chain().focus().setImage({ src: url }).run();
      } else {
        editor.chain().focus().insertContent({
          type: "video",
          attrs: { src: url },
        }).run();
      }
    }

    if (type === "image" && inputImgRef.current) inputImgRef.current.value = "";
    if (type === "video" && inputVideoRef.current) inputVideoRef.current.value = "";
  }

  // ✅ 유튜브 전용 아이콘 → 주소 입력 후 삽입
  function handleEmbedLink() {
    if (!editor) return;
    const url = window.prompt("임베드할 유튜브 주소를 입력하세요:");
    if (!url) return;

    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      editor.commands.setYoutubeVideo({ src: url });
      return;
    }

    // 그 외는 단순 링크 처리
    editor.chain().focus().insertContent(
      `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
    ).run();
  }

  const uploading = imageUploading || localUploading;
  if (!editor) return null;

  return (
    <div className="border rounded">
      {/* 툴바 */}
      <div className="flex gap-2 border-b p-2 bg-gray-50 flex-wrap items-center">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive("bold") ? "text-blue-600" : ""}
          title="굵게"
        >
          <Bold size={18} strokeWidth={2} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive("italic") ? "text-blue-600" : ""}
          title="기울임"
        >
          <Italic size={18} strokeWidth={2} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive("bulletList") ? "text-blue-600" : ""}
          title="목록"
        >
          <ListIcon size={18} strokeWidth={2} />
        </button>

        {/* 이미지 업로드 */}
        <label className="cursor-pointer flex items-center gap-1" title="이미지 첨부">
          <input
            ref={inputImgRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            disabled={uploading}
            onChange={(e) => handleMediaUpload(e, "image")}
          />
          <ImageIcon size={18} strokeWidth={2} />
          {uploading && <span className="text-xs text-blue-600 ml-1">업로드 중...</span>}
        </label>

        {/* 동영상 업로드 */}
        <label className="cursor-pointer flex items-center gap-1" title="동영상 첨부">
          <input
            ref={inputVideoRef}
            type="file"
            accept="video/*"
            style={{ display: "none" }}
            disabled={uploading}
            onChange={(e) => handleMediaUpload(e, "video")}
          />
          <VideoIcon size={18} strokeWidth={2} />
        </label>

        {/* 링크 */}
        <button
          type="button"
          onClick={() => {
            const url = window.prompt("URL을 입력하세요:");
            if (url) editor.chain().focus().setLink({ href: url }).run();
          }}
          className={editor.isActive("link") ? "text-blue-600" : ""}
          title="링크"
        >
          <LinkIcon size={18} strokeWidth={2} />
        </button>

        {/* 유튜브 전용 임베드 */}
        <button type="button" onClick={handleEmbedLink} title="유튜브 임베드">
          <YoutubeIcon size={20} strokeWidth={2} />
        </button>

        {/* 투표 추가 버튼 */}
        <button
          type="button"
          onClick={() => setShowPollModal(true)}
          className="ml-2"
          title="투표 추가"
        >
          <BarChartBig size={20} strokeWidth={2} />
        </button>
      </div>

      {showPollModal && (
        <PollModal
          initial={poll || undefined}
          onSave={(p) => {
            setPoll(p);
            if (onPollChange) onPollChange(p);
            setShowPollModal(false);
          }}
          onClose={() => setShowPollModal(false)}
        />
      )}

      <EditorContent editor={editor} className={`p-3 ${className || ""}`} />
      <style>{`
        .ProseMirror {
          min-height: 200px;
        }
        @media (min-width: 768px) {
          .ProseMirror {
            min-height: 400px;
          }
        }
        .ProseMirror:focus {
          outline: none;
        }
      `}</style>
    </div>
  );
}
